import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { FileText, X, Plus, Trash2, Wrench, Check } from 'lucide-react';

const CreateDevisFromRepairModal = ({ isOpen, device, onClose, onCreated }) => {
  const { api } = useAuth();
  const [lines, setLines] = useState([{ description: device?.problem || '', prix: 0, quantite: 1 }]);
  const [mainOeuvre, setMainOeuvre] = useState(0);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  if (!isOpen || !device) return null;

  const addLine = () => setLines([...lines, { description: '', prix: 0, quantite: 1 }]);
  const removeLine = (idx) => setLines(lines.filter((_, i) => i !== idx));
  const updateLine = (idx, field, value) => {
    const updated = [...lines];
    updated[idx][field] = value;
    setLines(updated);
  };

  const linesTotal = lines.reduce((s, l) => s + (parseFloat(l.prix) || 0) * (parseInt(l.quantite) || 1), 0);
  const total = linesTotal + (parseFloat(mainOeuvre) || 0);

  const handleCreate = async () => {
    const validLines = lines.filter(l => l.description.trim());
    if (!validLines.length) { setError('Ajoutez au moins une ligne'); return; }
    setCreating(true);
    setError('');
    try {
      await api.post(`/devices/${device.ref}/create-devis`, {
        lines: validLines.map(l => ({ description: l.description, prix: parseFloat(l.prix) || 0, quantite: parseInt(l.quantite) || 1 })),
        main_oeuvre: parseFloat(mainOeuvre) || 0,
      });
      setSuccess('Devis cree et lie !');
      setTimeout(() => { onCreated && onCreated(); onClose(); }, 1000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la creation');
    } finally { setCreating(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm px-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl border border-white/[0.08] shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
        style={{ background: 'linear-gradient(145deg, rgba(18,18,28,0.99), rgba(12,12,20,0.99))' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06] shrink-0" data-testid="create-devis-modal">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
              <FileText className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-white">Creer un devis</h3>
              <p className="text-[11px] text-gray-500">{device.ref} - {device.model}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-xl bg-white/[0.06] hover:bg-white/10 flex items-center justify-center text-gray-400 hover:text-white transition-all">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Client info */}
        <div className="px-5 py-3 border-b border-white/[0.06] shrink-0">
          <div className="flex items-center gap-3 text-xs text-gray-400">
            <Wrench className="w-4 h-4 text-orange-400" />
            <span><strong className="text-white">{device.client_name}</strong> - {device.client_phone}</span>
          </div>
        </div>

        {/* Messages */}
        {error && <div className="mx-5 mt-3 px-3 py-2 rounded-lg bg-red-500/10 text-red-400 text-xs">{error}</div>}
        {success && <div className="mx-5 mt-3 px-3 py-2 rounded-lg bg-emerald-500/10 text-emerald-400 text-xs flex items-center gap-2"><Check className="w-4 h-4" />{success}</div>}

        {/* Lines */}
        <div className="px-5 py-3 flex-1 overflow-y-auto space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-gray-300">Lignes du devis</span>
            <button onClick={addLine} className="px-2.5 py-1 rounded-lg bg-emerald-500/20 text-emerald-400 text-[10px] font-bold hover:bg-emerald-500/30 flex items-center gap-1" data-testid="add-devis-line">
              <Plus className="w-3 h-3" /> Ligne
            </button>
          </div>

          {lines.map((line, idx) => (
            <div key={idx} className="flex gap-2 items-start p-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
              <div className="flex-1 space-y-2">
                <input
                  type="text"
                  placeholder="Description"
                  value={line.description}
                  onChange={e => updateLine(idx, 'description', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-sm bg-white/[0.04] border border-white/[0.06] text-white placeholder-gray-600 focus:border-white/20 outline-none"
                  data-testid={`devis-line-desc-${idx}`}
                />
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="text-[10px] text-gray-500 mb-0.5 block">Prix (EUR)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={line.prix}
                      onChange={e => updateLine(idx, 'prix', e.target.value)}
                      className="w-full px-3 py-1.5 rounded-lg text-sm bg-white/[0.04] border border-white/[0.06] text-white focus:border-white/20 outline-none"
                      data-testid={`devis-line-prix-${idx}`}
                    />
                  </div>
                  <div className="w-20">
                    <label className="text-[10px] text-gray-500 mb-0.5 block">Qte</label>
                    <input
                      type="number"
                      min="1"
                      value={line.quantite}
                      onChange={e => updateLine(idx, 'quantite', e.target.value)}
                      className="w-full px-3 py-1.5 rounded-lg text-sm bg-white/[0.04] border border-white/[0.06] text-white focus:border-white/20 outline-none"
                      data-testid={`devis-line-qty-${idx}`}
                    />
                  </div>
                </div>
              </div>
              {lines.length > 1 && (
                <button onClick={() => removeLine(idx)} className="mt-2 w-7 h-7 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 flex items-center justify-center shrink-0">
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}

          {/* Main d'oeuvre */}
          <div className="pt-2">
            <label className="text-xs font-bold text-gray-300 mb-1.5 block">Main d'oeuvre (EUR)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={mainOeuvre}
              onChange={e => setMainOeuvre(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm bg-white/[0.04] border border-white/[0.06] text-white focus:border-white/20 outline-none"
              data-testid="devis-main-oeuvre"
            />
          </div>
        </div>

        {/* Footer with total */}
        <div className="px-5 py-4 border-t border-white/[0.06] shrink-0">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-gray-400">Total du devis</span>
            <span className="text-lg font-bold text-emerald-400">{total.toFixed(2)} EUR</span>
          </div>
          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-white/[0.06] hover:bg-white/10 text-gray-300 text-sm font-semibold transition-all">
              Annuler
            </button>
            <button
              onClick={handleCreate}
              disabled={creating}
              className="flex-1 py-2.5 rounded-xl text-white text-sm font-bold transition-all disabled:opacity-50"
              style={{ background: 'var(--theme-primary, #dc2626)' }}
              data-testid="create-devis-submit"
            >
              {creating ? '...' : 'Creer et lier'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateDevisFromRepairModal;
